import { css } from 'lit';

export const dashboardStyles = css`
      :host {
        display: block;
        padding: var(--space-6);
        background: transparent;
        min-height: 100%;
      }

      /* ─── Page Header ─── */
      .dashboard-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-8);
      }

      .dashboard-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      .dashboard-subtitle {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        margin-top: var(--space-1);
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .live-indicator {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-4);
        background: #d5e1d98a;
        border-radius: var(--radius-full);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-success-text);
      }

      .live-dot {
        width: 8px;
        height: 8px;
        background: var(--color-success);
        border-radius: var(--radius-full);
        animation: softPulse 2.5s ease-in-out infinite;
      }

      /* ─── Dashboard Grid ─── */
      .dashboard-grid {
        display: grid;
        gap: var(--space-8);
      }

      /* ─── KPI Cards Row ─── */
      .kpi-row {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-6);
      }

      .kpi-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-top: 3px solid var(--kpi-accent);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        box-shadow:
          0 8px 32px rgba(0, 0, 0, 0.06),
          inset 0 1px 0 rgba(255, 255, 255, 0.5),
          inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        transition: all var(--transition-base);
        position: relative;
        overflow: hidden;
        animation: slideUp var(--duration-slow) var(--ease-out) both;
      }

      /* Frost texture — SVG noise grain overlay */
      .kpi-card::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='f'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.35' numOctaves='6' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23f)' opacity='0.12'/%3E%3C/svg%3E");
        background-size: 512px 512px;
        pointer-events: none;
        border-radius: inherit;
        mix-blend-mode: overlay;
      }

      /* Top light sweep */
      .kpi-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 50%;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
        pointer-events: none;
        z-index: 1;
      }

      .kpi-card:nth-child(1) { --kpi-accent: var(--kpi-teal); animation-delay: 0ms; }
      .kpi-card:nth-child(2) { --kpi-accent: var(--kpi-amber); animation-delay: 80ms; }
      .kpi-card:nth-child(3) { --kpi-accent: var(--kpi-slate); animation-delay: 160ms; }
      .kpi-card:nth-child(4) { --kpi-accent: var(--kpi-sage); animation-delay: 240ms; }

      .kpi-card:hover {
        background: rgba(255, 255, 255, 0.3);
        box-shadow:
          0 12px 40px rgba(0, 0, 0, 0.08),
          inset 0 1px 0 rgba(255, 255, 255, 0.6),
          inset 0 -1px 0 rgba(255, 255, 255, 0.12);
        border-color: rgba(255, 255, 255, 0.5);
        transform: scale(1.01);
      }

      .kpi-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }

      .kpi-icon {
        width: 44px;
        height: 44px;
        border-radius: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.1rem;
        flex-shrink: 0;
        transition: transform 0.2s ease;
      }

      .kpi-card:hover .kpi-icon {
        transform: scale(1.05);
      }

      .kpi-card:nth-child(1) .kpi-icon {
        background: rgba(45, 143, 111, 0.1);
        color: var(--kpi-teal);
        border: 1px solid rgba(45, 143, 111, 0.1);
      }

      .kpi-card:nth-child(2) .kpi-icon {
        background: rgba(196, 149, 106, 0.1);
        color: var(--kpi-amber);
        border: 1px solid rgba(196, 149, 106, 0.1);
      }

      .kpi-card:nth-child(3) .kpi-icon {
        background: rgba(107, 127, 163, 0.1);
        color: var(--kpi-slate);
        border: 1px solid rgba(107, 127, 163, 0.1);
      }

      .kpi-card:nth-child(4) .kpi-icon {
        background: rgba(126, 163, 123, 0.1);
        color: var(--kpi-sage);
        border: 1px solid rgba(126, 163, 123, 0.1);
      }

      .kpi-trend {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
        padding: 2px 8px;
        border-radius: var(--radius-full);
      }

      .kpi-trend.positive {
        background: var(--color-success-bg);
        color: var(--color-success-text);
      }

      .kpi-trend.negative {
        background: var(--color-error-bg);
        color: var(--color-error-text);
      }

      .kpi-value {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        line-height: var(--leading-tight);
        letter-spacing: var(--tracking-tighter);
        margin-bottom: var(--space-1);
      }

      .kpi-value-row {
        display: flex;
        align-items: baseline;
        gap: var(--space-2);
      }

      .kpi-label {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        font-weight: var(--font-medium);
      }

      .kpi-pulse-dot {
        display: inline-block;
        width: 8px;
        height: 8px;
        background: var(--color-success);
        border-radius: var(--radius-full);
        margin-left: var(--space-2);
        animation: softPulse 2s ease-in-out infinite;
        vertical-align: middle;
      }

      /* ─── Two Column Rows ─── */
      .two-col {
        display: grid;
        grid-template-columns: 2fr 1fr;
        gap: var(--space-6);
      }

      .panel {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        box-shadow:
          0 8px 32px rgba(0, 0, 0, 0.06),
          inset 0 1px 0 rgba(255, 255, 255, 0.5),
          inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        overflow: hidden;
        animation: slideUp var(--duration-slow) var(--ease-out) both;
        position: relative;
      }

      /* Frost texture — SVG noise grain overlay */
      .panel::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='f'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.35' numOctaves='6' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23f)' opacity='0.12'/%3E%3C/svg%3E");
        background-size: 512px 512px;
        pointer-events: none;
        border-radius: inherit;
        mix-blend-mode: overlay;
      }

      /* Top light sweep */
      .panel::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 50%;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
        pointer-events: none;
        z-index: 1;
      }

      .panel-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-6) var(--space-6) var(--space-5);
      }

      .panel-title {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .panel-body {
        padding: 0 var(--space-6) var(--space-6);
      }

      /* ─── Bar Chart ─── */
      .chart-controls {
        display: flex;
        gap: var(--space-2);
      }

      .chart-area {
        display: flex;
        align-items: flex-end;
        gap: 3px;
        height: 200px;
        padding-top: var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
      }

      .chart-bar-wrapper {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;
        justify-content: flex-end;
      }

      .chart-bar {
        width: 100%;
        max-width: 24px;
        border-radius: 4px 4px 0 0;
        background: #d4eedf;
        transition: all var(--transition-base);
        min-height: 2px;
        position: relative;
      }

      .chart-bar:hover {
        background: #2D8F6F;
        box-shadow: 0 -4px 12px rgba(45, 143, 111, 0.2);
      }

      .chart-bar.current-hour {
        background: #2D8F6F;
      }

      .chart-bar-wrapper:hover .chart-tooltip {
        opacity: 1;
        transform: translateY(-2px);
      }

      .chart-tooltip {
        position: absolute;
        top: -28px;
        left: 50%;
        transform: translateX(-50%) translateY(0);
        background: var(--color-text-primary);
        color: var(--color-text-inverse);
        font-family: var(--font-mono);
        font-size: 10px;
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        transition: all var(--transition-fast);
      }

      .chart-labels {
        display: flex;
        gap: 3px;
        margin-top: var(--space-2);
      }

      .chart-label {
        flex: 1;
        text-align: center;
        font-family: var(--font-mono);
        font-size: 9px;
        color: var(--color-text-tertiary);
      }

      /* ─── Activity Feed ─── */
      .activity-feed {
        max-height: 280px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .activity-item {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        padding: var(--space-3);
        border-radius: var(--radius-md);
        transition: background var(--transition-fast);
      }

      .activity-item:hover {
        background: var(--color-bg-hover);
      }

      .activity-icon {
        width: 32px;
        height: 32px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-sm);
        flex-shrink: 0;
      }

      .activity-icon.call-in {
        background: var(--color-success-bg);
        color: var(--color-success-text);
      }

      .activity-icon.call-out {
        background: var(--color-accent-subtle);
        color: var(--color-accent);
      }

      .activity-icon.call-end {
        background: var(--color-bg-tertiary);
        color: var(--color-text-tertiary);
      }

      .activity-icon.transfer {
        background: var(--color-info-bg);
        color: var(--color-info-text);
      }

      .activity-icon.alert {
        background: var(--color-warning-bg);
        color: var(--color-warning-text);
      }

      .activity-body {
        flex: 1;
        min-width: 0;
      }

      .activity-text {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-primary);
        line-height: var(--leading-normal);
      }

      .activity-text strong {
        font-weight: var(--font-semibold);
      }

      .activity-time {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        margin-top: 2px;
      }

      /* ─── Active Calls List ─── */
      .active-calls-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      .active-call-row {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        padding: var(--space-3) var(--space-4);
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: var(--radius-md);
        transition: all var(--transition-fast);
      }

      .active-call-row:hover {
        border-color: rgba(255, 255, 255, 0.5);
        background: rgba(255, 255, 255, 0.35);
      }

      .call-status-dot {
        width: 10px;
        height: 10px;
        border-radius: var(--radius-full);
        background: var(--color-success);
        box-shadow: 0 0 0 3px var(--color-success-bg);
        animation: softPulse 2.5s ease-in-out infinite;
        flex-shrink: 0;
      }

      .call-caller {
        flex: 1;
        min-width: 0;
      }

      .call-caller-number {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
      }

      .call-caller-agent {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        margin-top: 1px;
      }

      .call-duration-live {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
        min-width: 52px;
        text-align: right;
      }

      .call-sentiment {
        width: 10px;
        height: 10px;
        border-radius: var(--radius-full);
        flex-shrink: 0;
      }

      .call-sentiment.positive { background: var(--color-success); }
      .call-sentiment.neutral { background: var(--color-warning); }
      .call-sentiment.negative { background: var(--color-error); }

      /* ─── AI Insights ─── */
      .insights-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
      }

      .insight-card {
        padding: var(--space-4);
        border-radius: var(--radius-lg);
        position: relative;
        overflow: hidden;
        border: 1px solid rgba(0, 0, 0, 0.04);
        box-shadow: var(--shadow-xs);
      }

      .insight-card:nth-child(1) {
        background: linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-primary-subtle) 100%);
      }

      .insight-card:nth-child(2) {
        background: linear-gradient(135deg, var(--color-accent-light) 0%, var(--color-accent-subtle) 100%);
      }

      .insight-card:nth-child(3) {
        background: linear-gradient(135deg, var(--color-success-light) 0%, var(--color-success-bg) 100%);
      }

      .insight-icon {
        font-size: var(--text-lg);
        margin-bottom: var(--space-2);
      }

      .insight-title {
        font-family: var(--font-display);
        font-size: var(--text-sm);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        margin-bottom: var(--space-1);
      }

      .insight-text {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-secondary);
        line-height: var(--leading-relaxed);
      }

      /* ─── Performance Bar ─── */
      .performance-bar {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6) var(--space-8);
        box-shadow:
          0 8px 32px rgba(0, 0, 0, 0.06),
          inset 0 1px 0 rgba(255, 255, 255, 0.5),
          inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-6);
        animation: slideUp var(--duration-slow) var(--ease-out) both;
        animation-delay: 400ms;
        position: relative;
        overflow: hidden;
      }

      /* Frost texture on performance bar */
      .performance-bar::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='f'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.35' numOctaves='6' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23f)' opacity='0.12'/%3E%3C/svg%3E");
        background-size: 512px 512px;
        pointer-events: none;
        border-radius: inherit;
        mix-blend-mode: overlay;
      }

      /* Top light sweep on performance bar */
      .performance-bar::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 50%;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%);
        pointer-events: none;
        z-index: 1;
      }

      .perf-stat {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        padding: 0 var(--space-4);
        border-right: 1px solid var(--color-border-subtle);
      }

      .perf-stat:last-child {
        border-right: none;
      }

      .perf-icon {
        width: 40px;
        height: 40px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-base);
        background: var(--color-bg-tertiary);
        color: var(--color-text-secondary);
        flex-shrink: 0;
      }

      .perf-info {
        flex: 1;
        min-width: 0;
      }

      .perf-value {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        line-height: var(--leading-tight);
      }

      .perf-label {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        font-weight: var(--font-medium);
      }

      /* ─── Responsive ─── */
      @media (max-width: 1280px) {
        .kpi-row {
          grid-template-columns: repeat(2, 1fr);
        }
        .performance-bar {
          grid-template-columns: repeat(2, 1fr);
        }
        .perf-stat:nth-child(2) {
          border-right: none;
        }
        .perf-stat:nth-child(3),
        .perf-stat:nth-child(4) {
          padding-top: var(--space-4);
          border-top: 1px solid var(--color-border-subtle);
        }
      }

      @media (max-width: 1024px) {
        .two-col {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 640px) {
        :host {
          padding: var(--space-4);
        }
        .kpi-row {
          grid-template-columns: 1fr;
        }
        .performance-bar {
          grid-template-columns: 1fr;
        }
        .perf-stat {
          border-right: none;
          border-bottom: 1px solid var(--color-border-subtle);
          padding-bottom: var(--space-4);
        }
        .perf-stat:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
      }
`;
